/**
 * Imockup 工作区布局尺寸配置。
 * 统一管理默认值与约束，便于后续调试和快速调整。
 */
export const IMOCKUP_LAYOUT = {
  /** 左右分割条占位宽度（与 `ImockupPanelSplitter` 视觉宽度一致）。 */
  splitterVertical: 10,
  /** 左侧机型选择栏默认宽度。 */
  defaultLeftRail: 380,
  /** 右侧属性面板默认宽度。 */
  defaultRightRail: 400,
  /** 底部动画编辑器默认高度。 */
  defaultBottomTimeline: 360,
  /** 壳层宽度大于此值时按宽屏处理（较大侧栏默认与较高底栏上限）。 */
  layoutWideBreakpointPx: 1280,
  /**
   * 非宽屏下左右侧栏宽度之和占壳层宽度的上限（与几何上限取较小值），保证主舞台为主。
   */
  narrowMaxSideRailsSumRatio: 0.48,
  /** 非宽屏下底栏高度占壳层高度的上限。 */
  narrowMaxBottomTimelineRatio: 0.38,
  /** 宽屏下底栏高度占壳层高度的上限（沿用原行为）。 */
  wideMaxBottomTimelineRatio: 0.58,
  /** 非宽屏下左栏初始宽度（与 `clampLayout` 协同；与右栏之和须不超过比例上限）。 */
  narrowDefaultLeftRail: 258,
  /** 非宽屏下右栏初始宽度。 */
  narrowDefaultRightRail: 230,
  /** 非宽屏下底栏初始高度。 */
  narrowDefaultBottomTimeline: 240,
  /** 左侧机型选择栏最小宽度。 */
  minLeftRail: 200,
  /** 右侧属性面板最小宽度。 */
  minRightRail: 220,
  /** 中央舞台区域最小宽度。 */
  minCenterStage: 240,
  /** 底部动画编辑器最小高度。 */
  minBottomTimeline: 120,
  /** 顶部舞台区域最小高度。 */
  minTopStage: 160,
} as const

/**
 * 左右侧栏宽度之和允许的最大值（像素）：几何上限与非宽屏比例上限的交集。
 */
export function sideRailsMaxCombinedPx(shellWidthPx: number): number {
  const gap = IMOCKUP_LAYOUT.splitterVertical * 2
  const geoMax = shellWidthPx - gap - IMOCKUP_LAYOUT.minCenterStage
  if (shellWidthPx > IMOCKUP_LAYOUT.layoutWideBreakpointPx) return geoMax
  return Math.min(geoMax, Math.floor(shellWidthPx * IMOCKUP_LAYOUT.narrowMaxSideRailsSumRatio))
}

/**
 * 底栏动画区允许的最大高度（像素），与 `HomeView` 原 `maxBottom` 公式对齐。
 */
export function bottomTimelineMaxPx(shellHeightPx: number, shellWidthPx: number): number {
  const ratio =
    shellWidthPx > IMOCKUP_LAYOUT.layoutWideBreakpointPx
      ? IMOCKUP_LAYOUT.wideMaxBottomTimelineRatio
      : IMOCKUP_LAYOUT.narrowMaxBottomTimelineRatio
  return Math.max(
    IMOCKUP_LAYOUT.minBottomTimeline + 40,
    Math.min(
      shellHeightPx - IMOCKUP_LAYOUT.minTopStage - IMOCKUP_LAYOUT.splitterVertical,
      shellHeightPx * ratio,
    ),
  )
}

/**
 * 首次渲染时根据壳层宽度选择默认左/右/底像素（无 `window` 时退回宽屏默认）。
 */
export function initialShellPanelPixels(): {
  leftRailPx: number
  rightRailPx: number
  bottomTimelinePx: number
} {
  if (globalThis.window === undefined) {
    return {
      leftRailPx: IMOCKUP_LAYOUT.defaultLeftRail,
      rightRailPx: IMOCKUP_LAYOUT.defaultRightRail,
      bottomTimelinePx: IMOCKUP_LAYOUT.defaultBottomTimeline,
    }
  }
  const sw = globalThis.window.innerWidth
  if (sw <= IMOCKUP_LAYOUT.layoutWideBreakpointPx) {
    return {
      leftRailPx: IMOCKUP_LAYOUT.narrowDefaultLeftRail,
      rightRailPx: IMOCKUP_LAYOUT.narrowDefaultRightRail,
      bottomTimelinePx: IMOCKUP_LAYOUT.narrowDefaultBottomTimeline,
    }
  }
  return {
    leftRailPx: IMOCKUP_LAYOUT.defaultLeftRail,
    rightRailPx: IMOCKUP_LAYOUT.defaultRightRail,
    bottomTimelinePx: IMOCKUP_LAYOUT.defaultBottomTimeline,
  }
}
